import { useQuery } from '@tanstack/react-query';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import useApiClient from 'hooks/use-api-client';
import { getQueryClientInstance } from 'stores/rq/common/query-client-provider';

const PAGE_SIZE = 100;

const activeEnvironmentsQueryKey = (orgId: string) =>
  [{ orgId, scope: 'dashboard-query', variant: 'allActiveEnvironments' }] as const;

export const useAllActiveEnvironments = () => {
  const apiClient = useApiClient();
  const orgId = useCurrentOrganizationId();

  return useQuery({
    queryKey: activeEnvironmentsQueryKey(orgId),
    queryFn: async () => {
      const firstPage = await apiClient.dashboards.getPaginatedWidget({
        organizationId: orgId,
        queryName: 'paginatedActiveEnvironments',
        offset: '0',
        limit: String(PAGE_SIZE)
      });

      const allData = [...(firstPage.data || [])];
      const total = firstPage.total || 0;

      if (total <= PAGE_SIZE) return allData;

      const remainingPages = Math.ceil((total - PAGE_SIZE) / PAGE_SIZE);
      const pagePromises = Array.from({ length: remainingPages }, (_, i) =>
        apiClient.dashboards.getPaginatedWidget({
          organizationId: orgId,
          queryName: 'paginatedActiveEnvironments',
          offset: String((i + 1) * PAGE_SIZE),
          limit: String(PAGE_SIZE)
        })
      );

      const pages = await Promise.all(pagePromises);
      for (const page of pages) {
        allData.push(...(page.data || []));
      }

      return allData;
    }
  });
};

// Nothing invalidates this query on its own - callers that change environment tags (bulk or
// single) must call this once the change has landed, or the table shows stale chips until reload.
export const invalidateAllActiveEnvironments = (orgId: string) =>
  getQueryClientInstance().invalidateQueries({ queryKey: activeEnvironmentsQueryKey(orgId) });
